import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { Flame, ArrowUpRight, Check, MousePointer, Eye, Monitor, BarChart3, Play, Zap } from 'lucide-react';

const features = [
  { icon: Monitor, title: 'Session replay', text: 'Replay visitor sessions on the actual page snapshot captured by the tracker. See exactly where they clicked and scrolled.' },
  { icon: MousePointer, title: 'Click and scroll maps', text: 'Every click and scroll is recorded with viewport coordinates so you can spot the most engaging areas.' },
  { icon: Eye, title: 'Page snapshots', text: 'The tracker sends a snapshot of the page DOM at the start of each session for accurate visual replay.' },
  { icon: BarChart3, title: 'Page-level metrics', text: 'See recordings, event counts, and last activity grouped by page to prioritize improvements.' },
  { icon: Play, title: 'Export replays', text: 'Download event-level CSV for any session to analyze behavior in your own tools.' },
  { icon: Zap, title: 'Lightweight tracker', text: 'A single async script handles live visitors, heatmaps, and screen recording without slowing down your site.' },
];

const useCases = [
  { title: 'Conversion optimization', text: 'Find which buttons and links visitors actually click and where they lose interest before converting.' },
  { title: 'UX debugging', text: 'Replay broken sessions to see where the layout, form, or navigation causes confusion.' },
  { title: 'Content planning', text: 'Identify the pages that get the most attention and create more content around what already works.' },
];

const faqs = [
  { q: 'What is a heatmap in ZuriHub?', a: 'ZuriHub Heatmaps records visitor clicks, scrolls, and pageviews and lets you replay the session on the actual page snapshot captured at the start of the visit.' },
  { q: 'How much does the Heatmaps add-on cost?', a: 'Heatmaps is $15 per workspace per month when added to a Free, Pro, or Enterprise plan.' },
  { q: 'Do I need the visitor consent?', a: 'Yes. You must disclose the use of tracking in your own privacy and cookie notices and obtain any consent required by law. ZuriHub does not bypass browser consent controls.' },
  { q: 'How long is heatmap data stored?', a: 'Visitor events and snapshots are retained for 30 days and then deleted automatically. You can export any session as CSV before it is removed.' },
  { q: 'Does the tracker slow down my site?', a: 'No. The tracker is small, async, and sends data using the browser Beacon API where available so it does not block the page.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'ZuriHub Heatmaps & Session Replay',
    description: 'Record visitor clicks, scrolls, and pageviews and replay sessions on the actual page snapshot.',
    brand: { '@type': 'Brand', name: 'ZuriHub' },
    offers: {
      '@type': 'Offer',
      url: `${site.baseUrl}/heatmaps`,
      price: '15',
      priceCurrency: 'USD',
      priceValidUntil: '2027-12-31',
      availability: 'https://schema.org/InStock',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function Heatmaps() {
  return (
    <>
      <SEO
        title="Heatmaps & Session Replay"
        description="ZuriHub Heatmaps is a $15/workspace add-on that records clicks, scrolls, and pageviews and replays sessions on the actual page snapshot."
        path="/heatmaps"
        jsonLd={jsonLd}
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Flame className="h-4 w-4" /> Addon
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Heatmaps & <span className="gradient-text">session replay</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            See where visitors click, how far they scroll, and replay their full session on the real page snapshot.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Enable heatmaps <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">What Heatmaps does</h2>
            <p className="section-subtitle mx-auto mt-4">Turn visitor behavior into a visual story.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="section-title">How it works</h2>
              <p className="mt-4 text-lg text-slate-600">
                Heatmaps is an optional workspace add-on. When enabled, the same ZuriHub tracker that powers Live Visitors also records clicks, scrolls, and a snapshot of the page for each visitor session.
              </p>
              <ol className="mt-8 space-y-4">
                {[
                  'Enable the Heatmaps add-on in workspace settings.',
                  'The tracker records click and scroll events and a page DOM snapshot.',
                  'Open the Heatmaps page to see recent sessions grouped by page.',
                  'Replay any session to watch the events on the captured page snapshot.',
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">{i + 1}</span>
                    <span className="text-slate-700">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500">
                  <MousePointer className="h-5 w-5" />
                </div>
                <span className="font-bold">Session replay</span>
              </div>
              <div className="mt-6 rounded-2xl bg-slate-800 p-6">
                <p className="text-sm text-slate-300">Page snapshot loaded</p>
                <div className="mt-4 space-y-3">
                  {[
                    { type: 'click', label: 'click at (124, 392)' },
                    { type: 'scroll', label: 'scroll at (0, 480)' },
                    { type: 'click', label: 'click at (212, 580)' },
                  ].map((e, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl bg-slate-700/50 p-3 text-sm">
                      <span className="h-2 w-2 rounded-full bg-orange-400" />
                      <span className="font-medium text-white">{e.type}</span>
                      <span className="text-slate-400">{e.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="scale-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="scale-heading" className="section-title">Scalable event storage</h2>
              <p className="mt-4 text-lg text-slate-600">
                Heatmap events are stored in D1 with indexed session, page, and created_at columns. Snapshots are captured at the start of each session and capped in size to keep storage predictable. All events are deleted automatically after 30 days.
              </p>
              <ul className="mt-8 space-y-3">
                {['Indexed by session, page, and timestamp', 'Snapshots capped for predictable size', 'Paginated session list', '30-day auto-cleanup', 'CSV export for any session'].map((s) => (
                  <li key={s} className="flex items-start gap-2 text-slate-700">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" /> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-orange-500 to-orange-700 px-8 py-12 text-center text-white shadow-xl">
              <p className="text-sm font-bold uppercase tracking-wider text-orange-100">Heatmaps addon</p>
              <p className="mt-2 text-5xl font-extrabold">$15</p>
              <p className="mt-1 text-orange-100">per workspace / month</p>
              <p className="mt-4 text-sm text-orange-100">Includes session replay, click/scroll events, and page snapshots.</p>
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-bold text-orange-700 transition hover:bg-slate-100"
              >
                Start using it <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="use-cases-heading" className="section-title">Use cases</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Heatmaps help teams stop guessing and start improving the right parts of the site.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-100 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <p className="section-subtitle mx-auto mt-4 text-center max-w-2xl">
            Answers to the most common questions about ZuriHub Heatmaps.
          </p>
          <div className="mt-10 space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-slate-900 px-6 py-12 text-center text-white sm:px-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Start understanding every click</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Add Heatmaps to your workspace for $15/month and see exactly how visitors interact with your site.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-orange-700 transition hover:bg-slate-100"
          >
            Enable Heatmaps <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
